/**
 * Knowledge Base utility functions
 */

import type { QuotaInfo } from "@/types";

/**
 * Format file size in bytes to human-readable string
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Get quota state based on usage percentage
 */
export function getQuotaState(
  quota: QuotaInfo,
): "normal" | "warning" | "error" {
  const filePercent = quota.maxFiles > 0
    ? (quota.fileCount / quota.maxFiles) * 100
    : 0;
  const sizePercent = quota.maxSize > 0
    ? (quota.totalSize / quota.maxSize) * 100
    : 0;

  if (filePercent >= 100 || sizePercent >= 100) return "error";
  if (filePercent >= 80 || sizePercent >= 80) return "warning";
  return "normal";
}

/**
 * Get percentage used for quota display
 */
export function getQuotaPercentage(current: number, max: number): number {
  if (max <= 0) return 0;
  const safeCurrent = Math.max(0, current);
  return Math.min(100, Math.round((safeCurrent / max) * 100));
}
